import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import axios from 'axios'

interface BlogPost {
  id: number
  slug: string
  title: string
  content: string
  author: string
  published_at: string
  read_time: number
  image_url?: string
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`/api/blog/${slug}`, {
          params: { lang: i18n.language },
        })
        setPost(response.data)
      } catch (error) {
        console.error('Error fetching blog post:', error)
      } finally {
        setLoading(false)
      }
    }
    if (slug) {
      fetchPost()
    }
  }, [slug, i18n.language])

  if (loading) {
    return (
      <section className="pt-32 pb-24 min-h-screen">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl h-96" />
        </div>
      </section>
    )
  }

  if (!post) {
    return (
      <section className="pt-32 pb-24 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Post not found</h1>
          <Link to="/blog" className="text-primary hover:underline">
            {t('blog.back')}
          </Link>
        </div>
      </section>
    )
  }

  return (
    <article className="pt-32 pb-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-primary hover:underline mb-8"
        >
          <span className="material-icons">arrow_back</span>
          {t('blog.back')}
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          {post.image_url && (
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-96 object-cover rounded-3xl mb-8"
            />
          )}
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">{post.title}</h1>
          <div className="flex items-center gap-6 text-slate-600 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <span className="material-icons text-sm">person</span>
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-icons text-sm">calendar_today</span>
              <span>{new Date(post.published_at).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-icons text-sm">schedule</span>
              <span>{post.read_time} {t('blog.readTime')}</span>
            </div>
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg dark:prose-invert max-w-none"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </motion.div>
      </div>
    </article>
  )
}
